import { memo } from 'react';

import useTranslation from '@Queries/useTranslation';
import usePage from '@Queries/usePage';
import PageExpressDesign from './PageExpressDesign';

import type { FC } from 'react';
import type { ExpressDesignData, PageExpressDesignData } from './type';

const PageExpressDesignContainer: FC = () => {
  const { data } = usePage<PageExpressDesignData>();
  const { t } = useTranslation();

  const expressDesignData: ExpressDesignData = {
    usefullBlock: [
      {
        id: 1,
        img: 'icon1',
        text: t('usefullBlock.text1'),
      },
      {
        id: 2,
        img: 'icon2',
        text: t('usefullBlock.text2'),
      },
      {
        id: 3,
        img: 'icon3',
        text: t('usefullBlock.text3'),
      },
      {
        id: 4,
        img: 'icon4',
        text: t('usefullBlock.text4'),
      },
    ],
    tariffs: [
      {
        id: 1,
        title: t('tariffs.base.title'),
        period: t('tariffs.base.period'),
        img: 'picture1',
        typeName: t('types.private'),
        tabs: [
          {
            id: 1,
            name: t('tariffs.premises.1-room.name'),
            price: {
              value: 5300,
              currency: 'RUB',
            },
            tooltip: t('tariffs.premises.1-room.tooltip'),
          },
          {
            id: 2,
            name: t('tariffs.premises.2-room.name'),
            price: { value: 6800, currency: 'RUB' },
            tooltip: t('tariffs.premises.2-room.tooltip'),
          },
          {
            id: 3,
            name: t('tariffs.premises.3-room.name'),
            price: { value: 9000, currency: 'RUB' },
            tooltip: t('tariffs.premises.3-room.tooltip'),
          },
          {
            id: 4,
            name: t('tariffs.premises.room.name'),
            price: {
              value: 3800,

              currency: 'RUB',
            },
            tooltip: t('tariffs.premises.room.tooltip'),
          },
          {
            id: 5,
            name: t('tariffs.premises.studio.name'),
            price: { value: 4500, currency: 'RUB' },
            tooltip: t('tariffs.premises.studio.tooltip'),
          },
        ],
        advantages: [
          t('tariffs.base.advantages.1'),
          t('tariffs.base.advantages.2'),
          t('tariffs.base.advantages.3'),
          t('tariffs.base.advantages.4'),
        ],
        variant: 'private',
        example: {
          url: '/documents/express-design/tariff_basic.pdf',
          title: t('tariffs.download'),
        },
      },
      {
        id: 2,
        title: t('tariffs.optimal.title'),
        period: t('tariffs.optimal.period'),
        img: 'picture2',
        typeName: t('types.private'),
        tabs: [
          {
            id: 1,
            name: t('tariffs.premises.1-room.name'),
            price: { value: 9000, currency: 'RUB' },
            tooltip: t('tariffs.premises.1-room.tooltip'),
          },
          {
            id: 2,
            name: t('tariffs.premises.2-room.name'),
            price: { value: 11300, currency: 'RUB' },
            tooltip: t('tariffs.premises.2-room.tooltip'),
          },
          {
            id: 3,
            name: t('tariffs.premises.3-room.name'),
            price: { value: 13500, currency: 'RUB' },
            tooltip: t('tariffs.premises.3-room.tooltip'),
          },
          {
            id: 4,
            name: t('tariffs.premises.room.name'),
            price: { value: 5300, currency: 'RUB' },
            tooltip: t('tariffs.premises.room.tooltip'),
          },
          {
            id: 5,
            name: t('tariffs.premises.studio.name'),
            price: { value: 7500, currency: 'RUB' },
            tooltip: t('tariffs.premises.studio.tooltip'),
          },
        ],
        advantages: [
          t('tariffs.optimal.advantages.1'),
          t('tariffs.optimal.advantages.2'),
          t('tariffs.optimal.advantages.3'),
          t('tariffs.optimal.advantages.4'),
          t('tariffs.optimal.advantages.5'),
        ],
        variant: 'private',
        example: {
          url: '/documents/express-design/tariff_optimal.pdf',
          title: t('tariffs.download'),
        },
      },
      {
        id: 3,
        title: t('tariffs.business.title'),
        period: t('tariffs.business.period'),
        img: 'picture3',
        typeName: t('types.busines'),
        tabs: [
          {
            id: 1,
            name: t('tariffs.premises.cafe.name'),
            tooltip: t('tariffs.premises.cafe.tooltip'),
          },
          {
            id: 2,
            name: t('tariffs.premises.restaurant.name'),
            tooltip: t('tariffs.premises.restaurant.tooltip'),
          },
          {
            id: 3,
            name: t('tariffs.premises.office.name'),
            tooltip: t('tariffs.premises.office.tooltip'),
          },
          {
            id: 4,
            name: t('tariffs.premises.hotel.name'),
            tooltip: t('tariffs.premises.hotel.tooltip'),
          },
          {
            id: 5,
            name: t('tariffs.premises.shop.name'),
            tooltip: t('tariffs.premises.shop.tooltip'),
          },
          {
            id: 6,
            name: t('tariffs.premises.other.name'),
            tooltip: t('tariffs.premises.other.tooltip'),
          },
        ],
        advantages: [t('tariffs.business.advantages.1')],
        variant: 'business',
        example: {
          url: '/idei-i-trendy/family-eco-farm-4-hectares-overlooking-the-Oka-River',
          title: t('tariffs.example'),
        },
      },
    ],
    advantages: [
      {
        id: 1,
        title: t('advantages.advantage1.title'),
        description: t('advantages.advantage1.description'),
        img: 'picture1',
      },
      {
        id: 2,
        title: t('advantages.advantage2.title'),
        description: t('advantages.advantage2.description'),
        img: 'picture2',
      },
      {
        id: 3,
        title: t('advantages.advantage3.title'),
        description: t('advantages.advantage3.description'),
        img: 'picture3',
      },
      {
        id: 4,
        title: t('advantages.advantage4.title'),
        description: t('advantages.advantage4.description'),
        img: 'picture4',
      },
    ],
    process: [
      {
        id: 1,
        title: t('process.item1.title'),
        text: t('process.item1.text'),
      },
      {
        id: 2,
        title: t('process.item2.title'),
        text: t('process.item2.text'),
      },
      {
        id: 3,
        title: t('process.item3.title'),
        text: t('process.item3.text'),
      },
      {
        id: 4,
        title: t('process.item4.title'),
        text: t('process.item4.text'),
      },
    ],
    team: [
      {
        id: 1,
        name: t('team.person1.name'),
        quote: t('team.person1.quote'),
        photo: 'photo1',
      },
      {
        id: 2,
        name: t('team.person2.name'),
        quote: t('team.person2.quote'),
        photo: 'photo2',
      },
      {
        id: 3,
        name: t('team.person3.name'),
        quote: t('team.person3.quote'),
        photo: 'photo3',
      },
      {
        id: 4,
        name: t('team.person4.name'),
        quote: t('team.person4.quote'),
        photo: 'photo4',
      },
    ],
    ourWorks: [
      {
        src: 'image1',
        orientation: 'portrait',
      },
      {
        src: 'image2',
        orientation: 'landscape',
      },
      {
        src: 'image3',
        orientation: 'landscape',
      },
      {
        src: 'image4',
        orientation: 'landscape',
      },
      {
        src: 'image5',
        orientation: 'landscape',
      },
      {
        src: 'image6',
        orientation: 'portrait',
      },
      {
        src: 'image7',
        orientation: 'portrait',
      },
      {
        src: 'image8',
        orientation: 'landscape',
      },
      {
        src: 'image9',
        orientation: 'landscape',
      },
      {
        src: 'image10',
        orientation: 'landscape',
      },
      {
        src: 'image11',
        orientation: 'landscape',
      },
      {
        src: 'image12',
        orientation: 'portrait',
      },
    ],
  };

  return <PageExpressDesign expressDesignData={expressDesignData} data={data} />;
};

export default memo(PageExpressDesignContainer);
